import type { CanvasNode, Connection, HistoryItem, LightHistoryItem } from '../types';

/**
 * The nodes to write when a workspace is left: those changed since they were last saved to
 * the cloud, plus any marked as changed. Nodes are replaced, never edited in place, so a
 * node that is not the very object that was saved has changed.
 *
 * Null when that cannot be told (nothing is known to be saved yet) or when nodes were
 * removed: then the whole workspace is saved, which also clears what was removed.
 */
export const unsavedNodeIds = (
    saved: CanvasNode[] | null,
    current: CanvasNode[],
    alsoChanged: Iterable<string> = [],
): Set<string> | null => {
    if (!saved) return null;
    const currentIds = new Set(current.map(node => node.id));
    if (saved.some(node => !currentIds.has(node.id))) return null;
    const savedById = new Map(saved.map(node => [node.id, node]));
    const ids = new Set<string>();
    for (const id of alsoChanged) if (currentIds.has(id)) ids.add(id);
    for (const node of current) if (savedById.get(node.id) !== node) ids.add(node.id);
    return ids;
};

/**
 * The nodes known to be saved once a save has gone through. `snapshot` is the canvas as the save
 * took it and `written` the nodes it wrote (null: all of them). `savedNow` is what was noted as
 * saved in the meantime: a cloud update merged in while the save was under way has changed it,
 * and what it brought stays noted.
 */
export const savedNodesAfterSave = (
    savedNow: CanvasNode[] | null,
    snapshot: CanvasNode[],
    written: Set<string> | null,
): CanvasNode[] => {
    if (!written || !savedNow) return snapshot;
    const snapshotById = new Map(snapshot.map(node => [node.id, node]));
    const known = new Set(savedNow.map(node => node.id));
    return [
        ...savedNow.map(node => (written.has(node.id) ? snapshotById.get(node.id) ?? node : node)),
        ...snapshot.filter(node => written.has(node.id) && !known.has(node.id)),
    ];
};

/**
 * The nodes that stay marked as changed once a save has come back; `marked` is what was marked
 * when it started. A save the cloud did not take leaves every mark, so the next save sends
 * those changes again. One the cloud took leaves the nodes it wrote without their media,
 * because the upload failed (`unstored`: the cloud holds an empty string in its place): they
 * are not saved yet, and staying marked they are uploaded again by the next save, and are not
 * replaced by the cloud's copy meanwhile.
 */
export const marksAfterSave = (
    result: { cloud: 'saved' | 'failed' | 'off'; unstored?: string[] },
    marked: Iterable<string>,
): string[] => (result.cloud === 'failed' ? [...marked] : result.unstored ?? []);

/**
 * Whether the copy of a cloud workspace written to the folder after a save holds work the
 * cloud has not taken (`cloudPending` in the workspace file). It does when the cloud did not
 * take a save that carried changes of the workspace (`carriedChanges`: a save on leaving with
 * nothing unsaved carries none, only the view and the thumbnail). And it still does when the
 * copy on the canvas was such a copy to begin with and the cloud has not been heard yet
 * (`wasPending`): a save of some nodes that went through has not sent the rest.
 *
 * False says the cloud holds this very copy, and the file's date is then not looked at when
 * it is opened again (localCopyOfFile in canvasSync.ts). A save that did not try the cloud
 * (`cloud` 'off') cannot say either: it gives undefined, the file is written without the key,
 * and its date decides.
 */
export const cloudPendingAfterSave = (cloud: 'saved' | 'failed' | 'off', carriedChanges: boolean, wasPending: boolean): boolean | undefined => {
    if (wasPending || (cloud === 'failed' && carriedChanges)) return true;
    return cloud === 'off' ? undefined : false;
};

type History = (HistoryItem | LightHistoryItem)[];

/** What an open workspace holds besides its view: the arrays themselves, so a change is told by identity. */
export interface WorkspaceContents {
    nodes: CanvasNode[];
    connections: Connection[];
    history: History;
    name: string;
}

/** A workspace as it was loaded or last saved. `nodes` is null when it is not known which nodes are saved. */
export interface SavedWorkspace extends Omit<WorkspaceContents, 'nodes'> {
    nodes: CanvasNode[] | null;
}

export type SavePlan =
    /** The workspace is what was loaded or last saved */
    | { save: false }
    /**
     * `nodeIds`: the nodes to write; an empty set when only the workspace's details changed
     * (its connections, history or name); null for every node.
     * `historyChanged`: whether the history has to be sent again.
     */
    | { save: true; nodeIds: Set<string> | null; historyChanged: boolean };

/**
 * What an auto-save has to write. Opening a workspace is not an edit: while its nodes,
 * connections, history and name are the ones loaded or last saved, nothing is written.
 * A change of connections, history or name alone writes the workspace's details and no
 * node. Every node is written only when it is not known what is saved, or nodes were
 * removed (which also clears them from the cloud).
 */
export const whatToSave = (
    saved: SavedWorkspace | null,
    current: WorkspaceContents,
    alsoChanged: Iterable<string> = [],
): SavePlan => {
    if (!saved) return { save: true, nodeIds: null, historyChanged: true };
    const historyChanged = saved.history !== current.history;
    const nodeIds = unsavedNodeIds(saved.nodes, current.nodes, alsoChanged);
    if (nodeIds === null) return { save: true, nodeIds: null, historyChanged };
    const detailsChanged = historyChanged || saved.connections !== current.connections || saved.name !== current.name;
    if (nodeIds.size === 0 && !detailsChanged) return { save: false };
    return { save: true, nodeIds, historyChanged };
};
